import {
  FpBulkLookups,
  FpBulkPreviewRow,
  FpBulkResolvedIngredient,
  FpBulkRowSelections,
} from './super-admin-finish-product-bulk-upload.models';

export interface FpBulkIngredientTaxonomy {
  faoFoodGroupName: string;
  allergenIds: number[];
  allergenNames: string;
  allergenFreeIds: number[];
  intoleranceIds: number[];
  intoleranceNames: string;
  intoleranceFreeIds: number[];
  category?: string;
  insNumber?: string;
  /** IngredientRefs.Id when present on master detail. */
  ingredientRefId?: number;
}

export interface FpBulkIngredientEnricherDeps {
  bulkResolveIngredients: (
    names: string[]
  ) => Promise<Map<string, { id: number; name: string; ingredientRefId?: number | null } | null>>;
  getIngredientDetail: (ingredientId: number) => Promise<FpBulkIngredientTaxonomy | null>;
}

function normalizeNameKey(name: string): string {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function toPositiveIds(raw: any): number[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
}

function claimIds(list: any[] | undefined | null): number[] {
  return (list || [])
    .map((a: any) => Number(a?.id ?? a?.Id))
    .filter((id) => Number.isFinite(id) && id > 0);
}

function claimNames(list: any[] | undefined | null): string {
  return (list || [])
    .map((a: any) => String(a?.name || a?.Name || '').trim())
    .filter(Boolean)
    .join(', ');
}

/** Extract FAO / allergen / intolerance taxonomy from ingredient GetById payload. */
export function extractFpIngredientTaxonomyFromDetail(detail: any): FpBulkIngredientTaxonomy | null {
  if (!detail || typeof detail !== 'object') return null;

  const containsAllergens =
    detail.allergens?.contains || detail.Allergens?.Contains || [];
  const freeAllergens = detail.allergens?.free || detail.Allergens?.Free || [];
  const containsIntolerances =
    detail.intolerances?.contains || detail.Intolerances?.Contains || [];
  const freeIntolerances = detail.intolerances?.free || detail.Intolerances?.Free || [];

  const allergenIds = toPositiveIds(detail.allergenIds ?? detail.AllergenIds).length
    ? toPositiveIds(detail.allergenIds ?? detail.AllergenIds)
    : claimIds(containsAllergens);
  const allergenNames =
    claimNames(containsAllergens) ||
    String(detail.allergenNames || detail.AllergenNames || '').trim();

  const allergenFreeIds = toPositiveIds(detail.allergenFreeIds ?? detail.AllergenFreeIds).length
    ? toPositiveIds(detail.allergenFreeIds ?? detail.AllergenFreeIds)
    : claimIds(freeAllergens);

  const intoleranceIds = toPositiveIds(detail.intoleranceIds ?? detail.IntoleranceIds).length
    ? toPositiveIds(detail.intoleranceIds ?? detail.IntoleranceIds)
    : claimIds(containsIntolerances);
  const intoleranceNames =
    claimNames(containsIntolerances) ||
    String(detail.intoleranceNames || detail.IntoleranceNames || '').trim();

  const intoleranceFreeIds = toPositiveIds(
    detail.intoleranceFreeIds ?? detail.IntoleranceFreeIds
  ).length
    ? toPositiveIds(detail.intoleranceFreeIds ?? detail.IntoleranceFreeIds)
    : claimIds(freeIntolerances);

  const faoFoodGroupName = String(
    detail.foodGroupFAOName ||
      detail.FoodGroupFAOName ||
      detail.faoFoodGroupName ||
      detail.FaoFoodGroupName ||
      detail.foodGroupName ||
      detail.FoodGroupName ||
      ''
  ).trim();

  return {
    faoFoodGroupName,
    allergenIds,
    allergenNames,
    allergenFreeIds,
    intoleranceIds,
    intoleranceNames,
    intoleranceFreeIds,
    category: String(detail.category || detail.Category || '').trim() || undefined,
    insNumber: String(detail.insNumber || detail.InsNumber || '').trim() || undefined,
    ingredientRefId: (() => {
      const n = Number(detail.ingredientRefId ?? detail.IngredientRefId ?? 0);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    })(),
  };
}

export function applyTaxonomyToIngredient(
  ing: FpBulkResolvedIngredient,
  tax: FpBulkIngredientTaxonomy
): void {
  if (!ing.faoFoodGroupName && tax.faoFoodGroupName) {
    ing.faoFoodGroupName = tax.faoFoodGroupName;
  }
  if (!(ing.allergenIds || []).length && tax.allergenIds.length) {
    ing.allergenIds = [...tax.allergenIds];
  }
  if (!ing.allergenNames && tax.allergenNames) {
    ing.allergenNames = tax.allergenNames;
  }
  if (!(ing.allergenFreeIds || []).length && tax.allergenFreeIds.length) {
    ing.allergenFreeIds = [...tax.allergenFreeIds];
  }
  if (!(ing.intoleranceIds || []).length && tax.intoleranceIds.length) {
    ing.intoleranceIds = [...tax.intoleranceIds];
  }
  if (!ing.intoleranceNames && tax.intoleranceNames) {
    ing.intoleranceNames = tax.intoleranceNames;
  }
  if (!(ing.intoleranceFreeIds || []).length && tax.intoleranceFreeIds.length) {
    ing.intoleranceFreeIds = [...tax.intoleranceFreeIds];
  }
  if (!ing.category && tax.category) {
    ing.category = tax.category;
  }
  if (!ing.insNumber && tax.insNumber) {
    ing.insNumber = tax.insNumber;
  }
  if (
    (!ing.ingredientRefId || !/^\d+$/.test(String(ing.ingredientRefId))) &&
    tax.ingredientRefId &&
    tax.ingredientRefId > 0
  ) {
    ing.ingredientRefId = String(tax.ingredientRefId);
  }
  ing.taxonomyHydrated = true;
}

function mergeIdLists(
  existing: Array<string | number> | undefined,
  autoIds: number[]
): Array<string | number> {
  const set = new Set<string>();
  (existing || []).forEach((id) => {
    const s = String(id ?? '').trim();
    if (s) set.add(s);
  });
  autoIds.forEach((id) => {
    if (Number.isFinite(id) && id > 0) set.add(String(id));
  });
  return Array.from(set);
}

/**
 * Union ingredient allergen/intolerance IDs into product-level allergen Information
 * and Observed Contains lists (same idea as add-finish-maker sync).
 */
export function syncFpBulkAllergensFromIngredients(
  selections: FpBulkRowSelections,
  _lookups?: FpBulkLookups
): void {
  const allergenIdSet = new Set<number>();
  const intoleranceIdSet = new Set<number>();
  const allergenFreeSet = new Set<number>();
  const intoleranceFreeSet = new Set<number>();

  for (const ing of selections.ingredients || []) {
    (ing.allergenIds || []).forEach((id) => {
      const n = Number(id);
      if (Number.isFinite(n) && n > 0) allergenIdSet.add(n);
    });
    (ing.intoleranceIds || []).forEach((id) => {
      const n = Number(id);
      if (Number.isFinite(n) && n > 0) intoleranceIdSet.add(n);
    });
    (ing.allergenFreeIds || []).forEach((id) => {
      const n = Number(id);
      if (Number.isFinite(n) && n > 0) allergenFreeSet.add(n);
    });
    (ing.intoleranceFreeIds || []).forEach((id) => {
      const n = Number(id);
      if (Number.isFinite(n) && n > 0) intoleranceFreeSet.add(n);
    });
  }

  const allergenIds = Array.from(allergenIdSet);
  const intoleranceIds = Array.from(intoleranceIdSet);
  const allergenFreeIds = Array.from(allergenFreeSet);
  const intoleranceFreeIds = Array.from(intoleranceFreeSet);

  // Declared + Observed Contains (Allergen Information)
  selections.allergenDeclaredIds = mergeIdLists(selections.allergenDeclaredIds, allergenIds);
  selections.allergenContainsIds = mergeIdLists(selections.allergenContainsIds, allergenIds);
  // Intolerance Information
  selections.intoleranceContainsIds = mergeIdLists(
    selections.intoleranceContainsIds,
    intoleranceIds
  );
  // Free lists when ingredient master provides them
  if (allergenFreeIds.length) {
    selections.allergenFreeIds = mergeIdLists(selections.allergenFreeIds, allergenFreeIds);
  }
  if (intoleranceFreeIds.length) {
    selections.intoleranceFreeIds = mergeIdLists(
      selections.intoleranceFreeIds,
      intoleranceFreeIds
    );
  }
}

function needsTaxonomy(ing: FpBulkResolvedIngredient): boolean {
  const hasRef = /^\d+$/.test(String(ing.ingredientRefId || '').trim());
  // Always resolve ref when we only have master id (FP FK requires IngredientRefs.Id).
  if (!hasRef && /^\d+$/.test(String(ing.ingredientId || '').trim())) {
    return true;
  }
  if (ing.taxonomyHydrated) return false;
  return (
    !ing.faoFoodGroupName ||
    (!(ing.allergenIds || []).length && !ing.allergenNames) ||
    (!(ing.intoleranceIds || []).length && !ing.intoleranceNames)
  );
}

async function mapPool<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) || 1 }, async () => {
    while (cursor < items.length) {
      const index = cursor++;
      results[index] = await worker(items[index]);
    }
  });
  await Promise.all(runners);
  return results;
}

/**
 * Resolve unmatched ingredient names, fetch master taxonomy, and sync product allergen lists.
 */
export async function enrichFpBulkRowsFromIngredients(
  rows: FpBulkPreviewRow[],
  deps: FpBulkIngredientEnricherDeps,
  onProgress?: (done: number, total: number) => void
): Promise<void> {
  if (!rows?.length) return;

  // Ensure drafts are promoted before enrich (caller may also rebuild payload first).
  const allIngredients: FpBulkResolvedIngredient[] = [];
  rows.forEach((r) => {
    (r.selections?.ingredients || []).forEach((ing) => allIngredients.push(ing));
  });
  if (!allIngredients.length) return;

  const namesNeedingId: string[] = Array.from(
    new Set(
      allIngredients
        .filter((i) => !String(i.ingredientId || '').trim() || !/^\d+$/.test(String(i.ingredientId)))
        .map((i) => String(i.name || '').trim())
        .filter((n) => !!n)
    )
  );

  let resolvedMap = new Map<
    string,
    { id: number; name: string; ingredientRefId?: number | null } | null
  >();
  if (namesNeedingId.length) {
    resolvedMap = await deps.bulkResolveIngredients(namesNeedingId);
    for (const row of rows) {
      for (const ing of row.selections.ingredients || []) {
        if (String(ing.ingredientId || '').trim() && /^\d+$/.test(String(ing.ingredientId))) {
          continue;
        }
        const hit = resolvedMap.get(normalizeNameKey(ing.name));
        if (hit?.id) {
          // Keep master id for GetById / units; store ref separately for FP FK.
          ing.ingredientId = String(hit.id);
          if (Number(hit.ingredientRefId) > 0) {
            ing.ingredientRefId = String(hit.ingredientRefId);
          }
          if (hit.name) ing.name = hit.name;
          ing.isNew = false;
        }
      }
    }
  }

  const idSet = new Set<number>();
  rows.forEach((r) => {
    (r.selections?.ingredients || []).forEach((i) => {
      const id = Number(i.ingredientId);
      if (Number.isFinite(id) && id > 0) idSet.add(id);
    });
  });
  const uniqueIds: number[] = Array.from(idSet);

  const idsToFetch: number[] = uniqueIds.filter((id) =>
    rows.some((r) =>
      (r.selections.ingredients || []).some(
        (i) => Number(i.ingredientId) === id && needsTaxonomy(i)
      )
    )
  );

  const detailCache = new Map<number, FpBulkIngredientTaxonomy | null>();
  let done = 0;
  const total = idsToFetch.length || 1;
  onProgress?.(0, total);

  await mapPool(idsToFetch, 6, async (id: number) => {
    try {
      const tax = await deps.getIngredientDetail(id);
      detailCache.set(id, tax);
    } catch {
      detailCache.set(id, null);
    } finally {
      done += 1;
      onProgress?.(done, total);
    }
  });

  for (const row of rows) {
    for (const ing of row.selections.ingredients || []) {
      const id = Number(ing.ingredientId);
      if (!Number.isFinite(id) || id <= 0) continue;
      const tax = detailCache.get(id);
      if (tax) {
        applyTaxonomyToIngredient(ing, tax);
      } else if (detailCache.has(id)) {
        // Fetched but empty / failed — don't retry forever.
        ing.taxonomyHydrated = true;
      }
    }
    syncFpBulkAllergensFromIngredients(row.selections);
  }

  onProgress?.(total, total);
}
